import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import QrCodeIcon from "@mui/icons-material/QrCode";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import NoProductItemDetailFound from "./NoProductItemDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  PAGE_PORTAL_INVENTORY_PRODUCT_DETAIL,
  PORTAL_PREFIX
} from "@kivunova/kivufrontendcommon";
import {
  getProductItemDetailFields,
  PRODUCT_ITEM_DETAIL_FIELD_LABEL_FALLBACKS,
  isUserIdField
} from "./utils/productUtils";
import {
  useGetProductItemDetail,
  useDeleteProductItem
} from "../../hooks/api/useProductItem";
import { useGetProductDetail } from "../../hooks/api/useProduct";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import EditProductItemModal from "./EditProductItemModal";
import TransitionProductItemModal from "./TransitionProductItemModal";
import ProductItemQRDialog from "./ProductItemQRDialog";
import { isProductItemReservedOrSold } from "./utils/productItemLifecycle";
import { UserName } from "../common/UserName/UserName";
import InventoryPageHeader from "../common/PageHeader/InventoryPageHeader";
import DetailFieldGrid from "../common/DetailFieldGrid/DetailFieldGrid";
import ConfirmActionDialog from "../common/GuidanceDialog/ConfirmActionDialog";

const ProductItemDetail: React.FC = () => {
  const { itemId: id, productId: routeProductId } = useParams<{
    itemId: string;
    productId: string;
  }>();
  const navigate = useNavigate();
  const [openQRModal, setOpenQRModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openTransitionModal, setOpenTransitionModal] = useState(false);
  const { t } = useKivunovaTranslation();
  const deleteProductItem = useDeleteProductItem();

  const productItem = useSelector(
    (state: RootState) => state.productItems.productItemDetail
  );
  const productDetail = useSelector(
    (state: RootState) => state.products.productDetail
  );
  const getProductItemDetail = useGetProductItemDetail();
  const getProductDetail = useGetProductDetail();

  useEffect(() => {
    if (id) {
      getProductItemDetail.mutate({ id });
    }
  }, [id]);

  useEffect(() => {
    if (productItem?.productId) {
      getProductDetail.mutate({ id: productItem.productId });
    }
  }, [productItem?.productId]);

  const product =
    productDetail?.id === productItem?.productId ? productDetail : undefined;

  const itemLocked = isProductItemReservedOrSold(productItem?.state);

  const refreshItemDetail = () => {
    if (id) {
      getProductItemDetail.mutate({ id });
    }
  };

  const handleConfirmDelete = async () => {
    if (!productItem?.id) return;
    const ok = await deleteProductItem.mutateAsync({ id: productItem.id });
    if (ok) {
      const pid = routeProductId || productItem.productId;
      const path = PAGE_PORTAL_INVENTORY_PRODUCT_DETAIL.replace(
        ":productId",
        pid
      );
      navigate(`/${PORTAL_PREFIX}/${path}`);
    }
    setOpenDeleteConfirm(false);
  };

  const isLoading =
    getProductItemDetail.isPending ||
    (!!productItem?.productId && getProductDetail.isPending);

  if (isLoading) {
    return (
      <CommonLoader
        loadingMessage={t(
          "product-item-detail-loading",
          "Fetching product item details..."
        )}
      />
    );
  }

  if (!productItem) {
    return <NoProductItemDetailFound />;
  }

  const fields = getProductItemDetailFields(productItem, product, t);
  const detailRows = fields.map(([labelKey, value]) => ({
    label: t(
      labelKey,
      PRODUCT_ITEM_DETAIL_FIELD_LABEL_FALLBACKS[labelKey] ?? labelKey
    ),
    value: isUserIdField(labelKey) ? (
      <UserName userId={String(value ?? "")} />
    ) : (
      (value ?? "-")
    )
  }));

  const actionButtonSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "0.8125rem"
  };

  return (
    <Box sx={{ color: "text.primary" }}>
      <InventoryPageHeader
        title={productItem.variationName?.trim() || productItem.id}
        subtitle={`${t("product-item-detail-title", "Product Item")}: ${productItem.id}${product?.name ? ` · ${product.name}` : ""}`}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setOpenEditModal(true)}
              disabled={itemLocked}
              sx={{
                ...actionButtonSx,
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                "&:hover": {
                  borderColor: BRAND_HOVER,
                  backgroundColor: BRAND_HOVER,
                  color: "white"
                }
              }}
            >
              {t("edit", "Edit")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineOutlinedIcon />}
              onClick={() => setOpenDeleteConfirm(true)}
              disabled={itemLocked}
              sx={actionButtonSx}
            >
              {t("delete", "Delete")}
            </Button>
            <Button
              variant="contained"
              startIcon={<QrCodeIcon />}
              onClick={() => setOpenQRModal(true)}
              sx={{
                ...actionButtonSx,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("product-item-detail-view-qr-code", "View QR Code")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => setOpenTransitionModal(true)}
              sx={{
                ...actionButtonSx,
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                "&:hover": {
                  borderColor: BRAND_HOVER,
                  backgroundColor: BRAND_HOVER,
                  color: "white"
                }
              }}
            >
              {t("product-item-detail-transition-status", "Change status")}
            </Button>
          </>
        }
        backAction={{
          label: t("product-item-detail-back", "Back"),
          onClick: () => navigate(-1)
        }}
      />

      {itemLocked && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, px: 0.5 }}
        >
          {t(
            "product-item-locked-hint",
            "This item is reserved or sold and cannot be edited or deleted."
          )}
        </Typography>
      )}

      <Typography
        variant="subtitle2"
        fontWeight={700}
        color="text.secondary"
        sx={{ mb: 1.5, fontSize: "0.8125rem", letterSpacing: "0.04em" }}
      >
        {t("product-item-details-section-title", "ITEM DETAILS")}
      </Typography>
      <DetailFieldGrid
        rows={detailRows}
        data-testid="product-item-details-grid"
      />

      <ProductItemQRDialog
        open={openQRModal}
        onClose={() => setOpenQRModal(false)}
        itemId={productItem.id}
        productName={product?.name}
      />

      {productItem && !itemLocked && (
        <EditProductItemModal
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          item={productItem}
          onSaved={refreshItemDetail}
        />
      )}

      <ConfirmActionDialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteProductItem.isPending}
        title={t(
          "delete-product-item-confirm-title",
          "Remove this product item from inventory?"
        )}
        description={t(
          "delete-product-item-confirm-subtitle",
          "This cannot be undone. Reserved or sold items cannot be deleted."
        )}
        confirmLabel={t("delete", "Delete")}
        cancelLabel={t("cancel", "Cancel")}
      />

      <TransitionProductItemModal
        open={openTransitionModal}
        onClose={() => setOpenTransitionModal(false)}
        item={productItem}
        onSuccess={refreshItemDetail}
      />
    </Box>
  );
};

export default ProductItemDetail;
